import { getJS, getBundleDir } from '../bundle';
import { logger } from '../utils/logger';
import type { Message } from '../types';

const CHUNK_SIZE = 900 * 1024; // 900 KB chunks (leaving room for metadata, max 1MB per message)

/**
 * Handle incoming bundle_req messages and send chunked bundle response
 */
export async function handleBundleRequest(
  data: any,
  bundleDir: string | undefined,
  sendMessage: (message: Message) => void
): Promise<void> {
  try {
    const id = data.id || 'unknown';
    const fromId = data.from?.id;

    // Get bundle directory and load bundle
    const dir = getBundleDir(bundleDir);
    const js = getJS(dir);
    const bundleSize = Buffer.byteLength(js, 'utf8');

    logger.info(`Bundle size: ${(bundleSize / 1024).toFixed(2)} KB`);

    // Split bundle into chunks
    const totalChunks = Math.ceil(bundleSize / CHUNK_SIZE);
    logger.info(`Splitting bundle into ${totalChunks} chunks`);

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, js.length);
      const chunk = js.substring(start, end);
      const isComplete = i === totalChunks - 1;
      const progress = ((i + 1) / totalChunks) * 100;

      const chunkMessage: Message = {
        id: `${id}-chunk-${i}`,
        type: 'BUNDLE_CHUNK',
        from: { type: 'data-agent' },
        to: fromId ? { id: fromId } : undefined,
        payload: {
          chunk: chunk,
          chunkIndex: i,
          totalChunks: totalChunks,
          isComplete: isComplete,
          progress: parseFloat(progress.toFixed(2)),
        },
      };

      sendMessage(chunkMessage);
      logger.debug(`Sent chunk ${i + 1}/${totalChunks} (${progress.toFixed(2)}%)`);
    }

    logger.info('Bundle sending complete');
  } catch (error) {
    logger.error('Failed to handle bundle request:', error);

    // Send error response
    const errorMessage: Message = {
      id: data.id || 'unknown',
      type: 'BUNDLE_RES',
      from: { type: 'data-agent' },
      to: data.from?.id ? { id: data.from.id } : undefined,
      payload: {
        error: error instanceof Error ? error.message : 'Unknown error',
      },
    };

    sendMessage(errorMessage);
  }
}
